import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useLocalization, Localized } from "@fluent/react";
import { LoadingShim, RadioButtons } from "@narmi/design_system";
import Feature from "byzantine/src/Feature";
import User from "byzantine/src/User";
import { ContextForm, ProgressButtons, useFormData } from "cerulean";
import cc from "classcat";
import InternationalEnrollmentForm from "./InternationalEnrollmentForm/InternationalEnrollmentForm";
import AccountNumberField from "./Fields/AccountNumberField";
import DobField from "./Fields/DobField";
import EinField from "./Fields/EinField";
import EmailField from "./Fields/EmailField";
import TaxIdField from "./Fields/TaxIdField";
import ReCAPTCHAFormField from "../form/ReCAPTCHAFormField";
import useVisibleFields from "./hooks/useVisibleFields";
import usePrimaryChoice from "./PrimaryChoiceContainer/usePrimaryChoice";
import COUNTRY_CODES_WHICH_DO_NOT_ISSUE_FTIN from "./constants";

type TellUsMoreAboutYouProps = {
  updateUser: (user: User) => void;
  encodedPayload?: string;
  institution: API.Institution;
};

type VerifyStep = "citizenship_select" | "account_details";

const TellUsMoreAboutYou = ({
  updateUser,
  encodedPayload,
  institution
}: TellUsMoreAboutYouProps) => {
  const { l10n } = useLocalization();
  const { formData, onChange } = useFormData({});
  const isConsumerNprEnabled =
    institution?.features?.olb_nonpermanent_residents;
  const isNprEnabled =
    isConsumerNprEnabled ||
    institution?.features?.olb_nonpermanent_residents_business;
  const previousCitizenship = useRef(formData.user_citizenship);

  const [activeStep, setActiveStep] = useState<VerifyStep>(
    isNprEnabled ? "citizenship_select" : "account_details"
  );
  const [isDonePostingEncodedPayload, setIsDonePostingEncodedPayload] =
    useState(false);
  const isActiveStepCitizenshipSelect = isNprEnabled
    ? activeStep === "citizenship_select"
    : false;
  const allFields: React.ReactElement[] = [
    <TaxIdField key="tax" />,
    <DobField key="dob" />
  ];

  let recaptchaPublicKey;
  const recaptchaPublicKeyElement = document.getElementById(
    "recaptcha_public_key"
  );
  if (recaptchaPublicKeyElement) {
    recaptchaPublicKey = JSON.parse(
      recaptchaPublicKeyElement.textContent as string
    );
  }

  // if core doesn't support emails, add the email field as the first field
  const coreHasEmailsElement = document.getElementById("core_has_emails");
  const coreHasEmails =
    coreHasEmailsElement &&
    JSON.parse(coreHasEmailsElement.textContent as string);

  if (!coreHasEmails) allFields.unshift(<EmailField key="email" />);
  // if core prompts for account numbers during enrollment, add the account number field as the first field
  if (institution.prompt_account_number)
    allFields.unshift(<AccountNumberField key="account" />);

  // if this is a business enrollment, show the ein field as the final field
  if (formData.user_category === User.BUSINESS)
    allFields.push(<EinField key="ein" />);

  const showCaptcha = institution.show_enrollment_captcha;
  const organizedInputs = useMemo(() => {
    if (allFields.length >= 4) {
      const lastField = allFields.pop();
      const penultimateField = allFields.pop();
      const lastTwoFieldsOnSingleLine = (
        <div
          className={cc([
            "last-two-elements",
            isNprEnabled && "last-two-elements--international-form"
          ])}
          key="last_two_elements"
        >
          {penultimateField}
          {lastField}
        </div>
      );
      allFields.push(lastTwoFieldsOnSingleLine);
    }
    return allFields;
  }, [activeStep, allFields]);

  const primaryChoiceConfig = usePrimaryChoice({
    l10n,
    isActiveStepCitizenshipSelect,
    userCitizenship: formData.user_citizenship
  });

  const visibleFields = useVisibleFields({
    formData,
    coreHasEmails,
    institution
  });
  const inputFields = useMemo(() => {
    if (isActiveStepCitizenshipSelect) {
      return [];
    }

    return formData.user_citizenship === "us_citizen"
      ? organizedInputs
      : visibleFields;
  }, [
    formData.user_citizenship,
    isActiveStepCitizenshipSelect,
    organizedInputs,
    visibleFields
  ]);

  const nextHandler = async (callback: (error?: any) => void) => {
    if (isNprEnabled && activeStep === "citizenship_select") {
      setActiveStep("account_details");
      callback();
      return;
    }
    let enrolledUser;
    try {
      enrolledUser = await User.enroll(formData);
      callback();
      updateUser(enrolledUser);
    } catch (err: any) {
      if (err?.email && err?.id === "blank" && coreHasEmails) {
        callback(
          l10n.getString(
            "error-no-account",
            null,
            "We could not find an email address associated with this account, which is required for Digital Banking. Please contact Support."
          )
        );
      } else {
        callback(err);
      }
    }
  };

  const backHandler = () => {
    if (!isNprEnabled || isActiveStepCitizenshipSelect) {
      window.location.assign("/login");
    } else {
      setActiveStep("citizenship_select");
    }
  };

  const backLabel = useMemo(() => {
    if (!isNprEnabled || isActiveStepCitizenshipSelect) {
      return l10n.getString("button-cancel", null);
    } else {
      return l10n.getString("label-back", null);
    }
  }, [isNprEnabled, isActiveStepCitizenshipSelect]);

  const getDefaultUserCategory = useCallback(() => {
    if (institution.features?.personal_banking && isConsumerNprEnabled) {
      return { user_category: User.PERSONAL };
    } else {
      return { user_category: User.BUSINESS };
    }
  }, [institution.features?.personal_banking, isConsumerNprEnabled]);

  useEffect(() => {
    if (isNprEnabled) {
      onChange(getDefaultUserCategory());
    } else if (institution.features?.personal_banking) {
      // default to personal if FI has personal, even if FI also has business banking
      onChange({ user_category: User.PERSONAL });
    } else if (institution.features?.business_banking) {
      onChange({ user_category: User.BUSINESS });
    }
    if (!encodedPayload) {
      setIsDonePostingEncodedPayload(true);
      return;
    }
    User.enroll({ payload: encodedPayload })
      .then((u) => {
        updateUser(u);
      })
      .finally(() => {
        // whether this call succeeds or fails, we should redirect to /enroll and finish loading
        window.history.replaceState({}, "", "/enroll");
        setIsDonePostingEncodedPayload(true);
      });
  }, []);

  // We have to use useEffect here because ContextForm.Field applies the citizenship
  // field update after the radio button's own onChange, so resetting user_category
  // inline gets overwritten unless we wait for the committed user_citizenship change
  // and then clear it in response.
  useEffect(() => {
    if (
      previousCitizenship.current === "us_citizen" &&
      formData.user_citizenship === "foreign_national"
    ) {
      onChange(getDefaultUserCategory());
    }

    previousCitizenship.current = formData.user_citizenship;
  }, [formData.user_citizenship, onChange]);

  useEffect(() => {
    if (formData.user_category === User.PERSONAL) onChange({ ein: "" });
    if (isNprEnabled && formData.user_citizenship === "us_citizen")
      onChange({
        first_name: "",
        last_name: "",
        passport_country: "",
        passport_number: "",
        ftin: ""
      });
  }, [formData.user_category, formData.user_citizenship]);

  useEffect(() => {
    if (
      COUNTRY_CODES_WHICH_DO_NOT_ISSUE_FTIN.includes(formData.passport_country)
    )
      onChange({ ftin: "" });
  }, [formData.passport_country]);

  if (!isDonePostingEncodedPayload)
    return (
      <LoadingShim isLoading>
        <div style={{ height: "100vh" }} />
      </LoadingShim>
    );

  const contextFormMessages = {
    errorRequired: l10n.getString("error-required"),
    errorSummary: l10n.getString("error-form-summary"),
    errorGeneric: l10n.getString("error-form-generic"),
    errorCheckInfo: l10n.getString("error-check-info"),
    warnConfirm: l10n.getString("warn-confirm")
  };

  return (
    <>
      <h1>
        <Localized id="heading-about-you">Tell us more about you</Localized>
      </h1>
      <div className="margin--top--xs" data-testid="enrollment-page-subtitle">
        <Localized id="subheading-account-information"></Localized>
      </div>
      <ContextForm
        data={formData}
        onChange={onChange}
        messages={contextFormMessages}
      >
        {isNprEnabled ? (
          <InternationalEnrollmentForm
            primaryChoiceConfig={primaryChoiceConfig}
            fields={inputFields}
            showAccountDetailsHeading={!isActiveStepCitizenshipSelect}
          />
        ) : (
          <>
            <Feature
              features={institution.features}
              and={["personal_banking", "business_banking"]}
            >
              <div className="account-type-container question">
                <div className="margin--bottom--l fontWeight--bold">
                  <Localized id="label-your-account-type">
                    What type of account do you have?
                  </Localized>
                </div>
                <ContextForm.Field required>
                  <RadioButtons
                    field="user_category"
                    name="user_category"
                    options={{
                      [l10n.getString("label-personal", null, "Personal")]:
                        User.PERSONAL,
                      [l10n.getString("label-business", null, "Business")]:
                        User.BUSINESS
                    }}
                  />
                </ContextForm.Field>
              </div>
            </Feature>
            <div className="margin--top--xl fontWeight--bold margin--bottom--l">
              <Localized id="heading-account-details">
                Account details
              </Localized>
            </div>
            <>{organizedInputs}</>
          </>
        )}
        <ReCAPTCHAFormField
          showCaptcha={showCaptcha}
          recaptchaPublicKey={recaptchaPublicKey}
        />
        <div className="progress-buttons-container">
          <ProgressButtons
            onNext={nextHandler}
            onBack={backHandler}
            backLabel={backLabel}
          />
        </div>
      </ContextForm>
    </>
  );
};

export default TellUsMoreAboutYou;
